import {
  HttpApiEndpoint,
  HttpApiGroup,
  HttpApiSchema,
  OpenApi,
} from 'effect/http-api';
import { Effect, Schema } from 'effect';
import {
  SUBSCRIPTION_HISTORY_PROVIDERS,
  SUBSCRIPTION_HISTORY_EVENT_SOURCES,
} from './subscription-history';
import { AdminAuthMiddleware } from './middleware';
import { ForbiddenError, NotFoundError, InternalServerError } from './errors';

// =============================================================================
// Shared Schemas
// =============================================================================

const AdminUserBilling = Schema.Struct({
  stripeCustomerId: Schema.NullOr(Schema.String),
  stripeSubscriptionId: Schema.NullOr(Schema.String),
  stripeSubscriptionStatus: Schema.NullOr(Schema.String),
  stripeCurrentPeriodEnd: Schema.NullOr(Schema.String),
  stripeCancelAtPeriodEnd: Schema.NullOr(Schema.Boolean),
  stripeTrialEnd: Schema.NullOr(Schema.String),
  rcSubscriptionStatus: Schema.NullOr(Schema.String),
  rcCurrentPeriodEnd: Schema.NullOr(Schema.String),
  rcTrialEnd: Schema.NullOr(Schema.String),
  referralStartAt: Schema.NullOr(Schema.String),
  referralEndAt: Schema.NullOr(Schema.String),
  referredByUserId: Schema.NullOr(Schema.String),
  hasEverPaid: Schema.Boolean,
  lastSyncedStripeAt: Schema.NullOr(Schema.String),
  lastSyncedRevenueCatAt: Schema.NullOr(Schema.String),
});

// =============================================================================
// ListUsers
// =============================================================================

const ListUsersUrlParams = Schema.Struct({
  pageIndex: Schema.FiniteFromString.pipe(
    Schema.check(Schema.isInt()),
    Schema.check(Schema.isGreaterThanOrEqualTo(0)),
    Schema.withDecodingDefaultType(Effect.succeed(0)),
  ),
  pageSize: Schema.FiniteFromString.pipe(
    Schema.check(Schema.isInt()),
    Schema.check(Schema.isGreaterThanOrEqualTo(1)),
    Schema.check(Schema.isLessThanOrEqualTo(100)),
    Schema.withDecodingDefaultType(Effect.succeed(20)),
  ),
  sortField: Schema.Literals([
    'name',
    'email',
    'createdAt',
    'role',
    'subscriptionStatus',
  ]).pipe(
    Schema.withDecodingDefaultType(Effect.sync(() => 'createdAt' as const)),
  ),
  sortOrder: Schema.Literals(['asc', 'desc']).pipe(
    Schema.withDecodingDefaultType(Effect.sync(() => 'desc' as const)),
  ),
  search: Schema.optional(Schema.String),
  role: Schema.optional(Schema.Literals(['admin', 'user'])),
  banned: Schema.optional(Schema.Literals(['true', 'false'])),
  subscriptionStatus: Schema.optional(
    Schema.Literals(['active', 'trialing', 'canceled', 'none']),
  ),
});

export const AdminUserRow = Schema.Struct({
  id: Schema.String,
  name: Schema.String,
  email: Schema.String,
  emailVerified: Schema.Boolean,
  role: Schema.NullOr(Schema.String),
  banned: Schema.NullOr(Schema.Boolean),
  createdAt: Schema.String,
  // Most recent session creation; null for users with no session since the
  // Mongo→better-auth cutover (sessions were reset then).
  lastSignedInAt: Schema.NullOr(Schema.String),
  subscriptionStatus: Schema.NullOr(Schema.String),
  subscriptionPlatform: Schema.NullOr(Schema.String),
  hasEverPaid: Schema.Boolean,
});

const ListUsersResponse = Schema.Struct({
  users: Schema.Array(AdminUserRow),
  total: Schema.Int,
});

const listUsersEndpoint = HttpApiEndpoint.get('ListUsers', '/users', {
  query: ListUsersUrlParams,
  success: ListUsersResponse,
  error: InternalServerError,
})
  .middleware(AdminAuthMiddleware)

  .annotate(OpenApi.Summary, 'List users with filtering, sorting, pagination');

// =============================================================================
// GetUserDetail
// =============================================================================

const GetUserDetailResponse = Schema.Struct({
  id: Schema.String,
  name: Schema.String,
  email: Schema.String,
  emailVerified: Schema.Boolean,
  image: Schema.NullOr(Schema.String),
  role: Schema.NullOr(Schema.String),
  banned: Schema.NullOr(Schema.Boolean),
  banReason: Schema.NullOr(Schema.String),
  banExpires: Schema.NullOr(Schema.String),
  createdAt: Schema.String,
  updatedAt: Schema.String,
  referralCode: Schema.NullOr(Schema.String),
  deletedAt: Schema.NullOr(Schema.String),
  billing: Schema.NullOr(AdminUserBilling),
  preferences: Schema.NullOr(Schema.Struct({ voice: Schema.String })),
  marketingSurvey: Schema.NullOr(
    Schema.Struct({
      acquisitionSource: Schema.String,
      details: Schema.NullOr(Schema.String),
    }),
  ),
});

const getUserDetailEndpoint = HttpApiEndpoint.get(
  'GetUserDetail',
  '/users/:userId',
  {
    params: Schema.Struct({ userId: Schema.String }),
    success: GetUserDetailResponse,
    error: [NotFoundError, InternalServerError],
  },
)
  .middleware(AdminAuthMiddleware)

  .annotate(OpenApi.Summary, 'Get detailed user info including billing');

// =============================================================================
// SyncUserBilling
// =============================================================================

const SyncUserBillingResponse = Schema.Struct({
  billing: AdminUserBilling,
  stripeSynced: Schema.Boolean,
  revenueCatSynced: Schema.Boolean,
  errors: Schema.Array(Schema.String),
});

const syncUserBillingEndpoint = HttpApiEndpoint.post(
  'SyncUserBilling',
  '/users/:userId/sync-billing',
  {
    params: Schema.Struct({ userId: Schema.String }),
    success: SyncUserBillingResponse,
    error: [NotFoundError, InternalServerError],
  },
)
  .middleware(AdminAuthMiddleware)

  .annotate(
    OpenApi.Summary,
    'Refresh billing data from Stripe/RevenueCat for a user',
  );

// =============================================================================
// DeleteUserAccount
// =============================================================================

const deleteUserAccountEndpoint = HttpApiEndpoint.delete(
  'DeleteUserAccount',
  '/users/:userId',
  {
    params: Schema.Struct({ userId: Schema.String }),
    success: Schema.Void.pipe(HttpApiSchema.status(204)),
    error: [NotFoundError, ForbiddenError, InternalServerError],
  },
)
  .middleware(AdminAuthMiddleware)

  .annotate(
    OpenApi.Summary,
    'Soft-delete a user account, as the user deleting it themselves would',
  );

// =============================================================================
// GetUserSubscriptionHistory
// =============================================================================

const SubscriptionHistoryRow = Schema.Struct({
  id: Schema.String,
  provider: Schema.Literals([...SUBSCRIPTION_HISTORY_PROVIDERS]),
  eventType: Schema.String,
  eventSource: Schema.Literals([...SUBSCRIPTION_HISTORY_EVENT_SOURCES]),
  previousState: Schema.NullOr(Schema.Unknown),
  newState: Schema.NullOr(Schema.Unknown),
  periodEndAt: Schema.NullOr(Schema.String),
  providerEventId: Schema.NullOr(Schema.String),
  metadata: Schema.NullOr(Schema.Unknown),
  createdAt: Schema.String,
});
export type SubscriptionHistoryRow = typeof SubscriptionHistoryRow.Type;

const GetUserSubscriptionHistoryUrlParams = Schema.Struct({
  pageIndex: Schema.FiniteFromString.pipe(
    Schema.check(Schema.isInt()),
    Schema.check(Schema.isGreaterThanOrEqualTo(0)),
    Schema.withDecodingDefaultType(Effect.succeed(0)),
  ),
  pageSize: Schema.FiniteFromString.pipe(
    Schema.check(Schema.isInt()),
    Schema.check(Schema.isGreaterThanOrEqualTo(1)),
    Schema.check(Schema.isLessThanOrEqualTo(200)),
    Schema.withDecodingDefaultType(Effect.succeed(50)),
  ),
});

const GetUserSubscriptionHistoryResponse = Schema.Struct({
  events: Schema.Array(SubscriptionHistoryRow),
  total: Schema.Int,
});

const getUserSubscriptionHistoryEndpoint = HttpApiEndpoint.get(
  'GetUserSubscriptionHistory',
  '/users/:userId/subscription-history',
  {
    params: Schema.Struct({ userId: Schema.String }),
    query: GetUserSubscriptionHistoryUrlParams,
    success: GetUserSubscriptionHistoryResponse,
    error: InternalServerError,
  },
)
  .middleware(AdminAuthMiddleware)

  .annotate(
    OpenApi.Summary,
    'List subscription_history audit rows for a user (newest first)',
  );

// =============================================================================
// Group
// =============================================================================

export const adminGroup = HttpApiGroup.make('Admin')
  .add(listUsersEndpoint)
  .add(getUserDetailEndpoint)
  .add(syncUserBillingEndpoint)
  .add(deleteUserAccountEndpoint)
  .add(getUserSubscriptionHistoryEndpoint)
  .prefix('/admin');
